import React, { useContext, useEffect, useState } from "react";
import {
  CircularProgress,
  Typography,
  useTheme,
  Box,
  Button,
  Stack,
  Divider,
  Card,
  Switch,
} from "@mui/material";
import GridContainer from "../../components/grid/GridContainer.jsx";
import GridItem from "../../components/grid/GridItem.jsx";
import { Helmet } from "react-helmet";
import MuiDataGrid from "../../components/MuiDataGrid.jsx";
import { gridStringOrNumberComparator } from "@mui/x-data-grid";
import Search from "../../components/customcomponents/Search";
import { getDashboardStyle } from "../../components/customstyles/DashboardStyle";
import { AuthContext } from "../../context/AuthContext";
import { useHistory } from "react-router-dom";
import AssessmentAction from "./AssessmentAction";
import patientDashboard from "../../assets/images/patientDashboard.png";
import { calculateLastAssessment, formatDate } from "../utilsFunctions.js";
import { useGuidedMode } from "../../providers/GuidedModeProvider";

const useStyles = () => {
  const theme = useTheme();
  return getDashboardStyle(theme);
};

const lastAssessmentColumnSortFn = (a, b, order) => {
  if (a === null && b === null) return 0;
  if (a === null) return order === "asc" ? -1 : 1;
  if (b === null) return order === "asc" ? 1 : -1;

  const aDate = new Date(a);
  const bDate = new Date(b);

  if (order === "asc") {
    return bDate - aDate;
  }
  return aDate - bDate;
};

function PatientsDashboard() {
  const styles = useStyles();

  const [patientsData, setPatientsData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState([true]);
  const { makeAuthenticatedApiCall } = useContext(AuthContext);
  const history = useHistory();
  const { guidedMode, handleSwitchChange, refreshGuidedMode } = useGuidedMode();

  // table column defenition
  const commonColumnOptions = {
    resizable: false,
    aggregable: false,
    gropable: false,
    hideable: false,
    pinnable: false,
  };

  let columns = [
    {
      ...commonColumnOptions,
      field: "patientObj",
      flex: 1,
      minWidth: 160,
      sortComparator: (a, b, meta1, meta2) =>
        gridStringOrNumberComparator(
          a.patientName,
          b.patientName,
          meta1,
          meta2
        ),
      renderHeader: () => (
        <Typography sx={styles.tableColumnHeader}>Patient</Typography>
      ),
      renderCell: (params) => (
        <div style={{ height: "100%", width: "100%", paddingTop: "5px" }}>
          <div style={{ padding: "8px 0" }}>
            <Typography sx={styles.tablePatientName}>
              {params.value.patientName}
            </Typography>
            <Typography sx={styles.tablePatientDOBMRN}>
              {/* DOB:{convertDateFormat(params.value.patientDob)} */}
              DOB:{formatDate(params.value.patientDob)}
            </Typography>
            <Typography sx={{ ...styles.tablePatientDOBMRN }}>
              EHR Patient ID:{params.value.patientEmrId}
            </Typography>
          </div>
        </div>
      ),
    },
    {
      ...commonColumnOptions,
      field: "lastAssessment",
      flex: 1,
      minWidth: 170,
      sortComparator: (a, b, params) => {
        const order = params.sortDirection || "asc";
        return lastAssessmentColumnSortFn(a, b, order);
      },
      renderHeader: () => (
        <Typography sx={styles.tableColumnHeader}>Last Assessment</Typography>
      ),
      renderCell: (params) => (
        <div style={{ paddingTop: "4px" }}>
          <div style={{ height: "100%", width: "100%", padding: "10px 0" }}>
            <Typography
              sx={{
                ...styles.TableBodyText,
                padding: "10px 0",
              }}
            >
              {params.value === null
                ? "--"
                : calculateLastAssessment(params.value)}
            </Typography>
          </div>
        </div>
      ),
    },
    {
      ...commonColumnOptions,
      field: "aims",
      flex: 1,
      minWidth: 50,
      maxWidth: 200,
      renderHeader: () => (
        <Typography sx={styles.tableColumnHeader}>AIMS</Typography>
      ),
      renderCell: (params) => (
        <div style={{ paddingTop: "4px" }}>
          <div style={{ height: "100%", width: "100%", padding: "10px 0" }}>
            <Typography
              sx={{
                ...styles.TableBodyText,
                padding: "10px 0",
              }}
            >
              {params.value}
            </Typography>
          </div>
        </div>
      ),
    },
    {
      ...commonColumnOptions,
      field: "Action",
      flex: 1,
      minWidth: 160,
      maxWidth: 200,
      sortable: false,
      renderHeader: () => (
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <Switch
            checked={guidedMode !== false}
            onChange={handleSwitchChange}
            color="primary"
            size="small"
          />
          <Typography sx={styles.tableColumnHeader}>Guided Mode</Typography>
        </Box>
      ),
      renderCell: (params) => <AssessmentAction patientId={params.value} />,
    },
  ];

  const handleRowClick = ({ row }) => {
    localStorage.removeItem("addedThroughSource");
    history.push(`./patients/${row?.patientId}/1`);
  };

  const changePatientList = (selecteddPatientList) => {
    if (selecteddPatientList !== undefined) {
      if (selecteddPatientList.length > 0) {
        setTableData(selecteddPatientList);
      } else if (selecteddPatientList.length === 0) {
        setTableData([]);
      } else {
        setTableData(null);
      }
    }
  };

  useEffect(() => {
    const getPatientsList = async () => {
      refreshGuidedMode();
      let tableData = [];
      let response = await makeAuthenticatedApiCall(
        "get",
        `/api/userservice/getpatientslist`,
        {}
      );
      if (response?.status === 200 && response?.data?.length > 0) {
        response.data.forEach((item, index) => {
          tableData.push({
            id: `${item.patientId}-${index}`,
            patientId: item.patientId,
            patientObj: {
              patientName: item.patientName,
              patientDob: item.patientDOB,
              patientEmrId: item.patientEmrId,
            },
            lastAssessment: item.lastAssessmentDate,
            aims: item.totalAssessments,
            providerName: item.providerName,
            Action: item.patientId,
          });
        });
      }
      setLoading(false);
      setPatientsData(tableData);
    };
    if (patientsData.length === 0) {
      getPatientsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeAuthenticatedApiCall]);
  return (
    <Box sx={styles.patientTableStyle}>
      <Helmet>
        <title>Patients</title>
      </Helmet>
      <GridContainer>
        <GridItem lg={12} md={12} sm={12} xs={12} sx={{ marginTop: "20px" }}>
          <Typography sx={styles.patientTextStyle}>Patients</Typography>
        </GridItem>
        <GridItem
          lg={12}
          md={12}
          sm={12}
          xs={12}
          sx={{ marginTop: "15px", marginBottom: "25px" }}
        >
          <Divider />
        </GridItem>
      </GridContainer>

      {loading && (
        <Box sx={styles.loadingLeftStyle}>
          <Box sx={{ marginBottom: "20px" }}>
            <CircularProgress />
          </Box>
          <Typography sx={styles.loadinText}>
            Loading patients details...
          </Typography>
        </Box>
      )}
      {!loading && patientsData.length === 0 && (
        <GridContainer sx={styles.emptyDashboardStyle}>
          <GridItem sx={{ marginTop: "5%" }}>
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={3}
              sx={{
                height: "300px",
                textAlign: "center",
              }}
            >
              <GridContainer>
                <GridItem
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={styles.searchPatientStyle}
                >
                  <img
                    src={patientDashboard}
                    style={{
                      width: "50px",
                      height: "50px",
                      marginBottom: "20px",
                    }}
                    alt={""}
                  />
                </GridItem>
                <GridItem
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={styles.searchPatientStyle}
                >
                  <Typography sx={styles.patientDashboardNoPatient}>
                    No Patients Yet
                  </Typography>
                  <Typography sx={styles.patientDashboardText}>
                    Add patient to begin AIMS Assessment
                  </Typography>
                </GridItem>
                <GridItem
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ marginTop: "20px" }}
                >
                  <Button
                    variant="contained"
                    sx={styles.addPatientButton}
                    onClick={() => {
                      localStorage.removeItem("addedThroughSource");
                      history.push("/patients/add");
                    }}
                  >
                    Add Patient
                  </Button>
                </GridItem>
              </GridContainer>
            </Stack>
          </GridItem>
        </GridContainer>
      )}
      {patientsData.length > 0 && (
        <Card sx={styles.tableContentStyle}>
          <GridContainer>
            <GridItem lg={4} md={4} sm={12} xs={12} sx={styles.gridAlign}>
              {/* <p style={styles.patientHeading}>Patients</p> */}
              <Search
                pathToSearch={"patientObj.patientName"}
                searchPlaceholder={"Search Patients"}
                list={patientsData}
                filterList={changePatientList}
              />
            </GridItem>
            <GridItem
              lg={8}
              md={8}
              sm={12}
              xs={12}
              sx={{
                ...styles.gridAlign,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Button
                variant="contained"
                sx={styles.addPatientButton}
                onClick={() => {
                  localStorage.removeItem("addedThroughSource");
                  history.push("/patients/add");
                }}
              >
                Add Patient
              </Button>
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem md={12}>
              <MuiDataGrid
                rows={tableData}
                columns={columns}
                onRowClick={handleRowClick}
                getRowHeight={() => "auto"}
                slotProps={{
                  row: {
                    style: { cursor: "pointer" },
                  },
                }}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10, page: 0 },
                  },
                }}
                pageSizeOptions={[10, 20, 50]}
                autoPageSize={false}
              />
            </GridItem>
          </GridContainer>
        </Card>
      )}
    </Box>
  );
}
export default PatientsDashboard;
